import { audio } from '../systems/audio/AudioSystem.js';
import { BOSSES } from '../data/bosses.js';
import { requestLandscape } from '../core/deviceMode.js';

/**
 * Landing screen: title, Echoes balance, mute toggle, and three vertical
 * entry points — Play (run/leaderboard), Shop, Settings (everything else,
 * see ui/PlayMenu.js and ui/SettingsHubMenu.js for what each expands into).
 * Pure DOM overlay — no canvas/game state, mounted straight into #ui-root
 * before any run exists. WASD/Enter keyboard navigation comes from the
 * global ui/menuKeyNav.js, like every other menu screen.
 */
export class MainMenu {
  constructor(root, save, { onPlay, onShop, onSettingsHub }) {
    this.root = root;
    this.save = save;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay main-menu-overlay';
    this.el.innerHTML = `
      <button type="button" class="mute-toggle" data-action="mute" aria-label="Toggle sound">${save.muted ? '\u{1F507}' : '\u{1F50A}'}</button>
      <button type="button" class="fullscreen-toggle" data-action="fullscreen" aria-label="Toggle fullscreen" title="Fullscreen — removes the browser/OS chrome that can clip overlays">${document.fullscreenElement ? '\u{1F5D9}' : '\u{26F6}'}</button>
      <div class="menu-command-deck">
        <header class="menu-command-header">
          <div class="menu-kicker">THE UNMADE GAUNTLET</div>
          <div class="menu-title">SURVIVOR</div>
        </header>
        <div class="menu-command-grid">
          <section class="menu-panel menu-panel-preview">
            <div class="menu-preview-boss">${BOSSES[0].name}</div>
            <div class="menu-preview-rule">Control · positioning · rotating sanctums</div>
            <div class="menu-boss-track">${BOSSES.map((boss, i) => `<span class="menu-boss-pip ${i === 0 ? 'menu-boss-pip-active' : ''}" title="${boss.name}">${i + 1}</span>`).join('')}</div>
            <div class="menu-preview-meta"><span>GAUNTLET</span><strong>${BOSSES.length} BOSSES</strong></div>
            <div class="menu-preview-meta"><span>MODE</span><strong>NORMAL / ASCENSION</strong></div>
          </section>
          <section class="menu-panel menu-panel-actions">
            <button type="button" class="menu-button menu-button-primary menu-button-start" data-action="play">ENTER GAUNTLET <span>↵</span></button>
            <button type="button" class="menu-button" data-action="shop">Armory & Collection</button>
            <button type="button" class="menu-button" data-action="settings-hub">Settings & Records</button>
          </section>
        </div>
        <footer class="menu-command-footer">
          <div class="menu-currency-strip"><span>◈ <b class="menu-echoes-value">${save.echoes}</b> Echoes</span><span>✦ <b class="menu-shards-value">${save.shards}</b> Shards</span><span>❖ <b class="menu-glimmer-value">${save.glimmer}</b> Glimmer</span></div>
          <div class="menu-hint">WASD / ARROWS TO NAVIGATE · ENTER TO SELECT</div>
        </footer>
      </div>
      <div class="menu-version">v${__APP_VERSION__}</div>
    `;
    // __APP_VERSION__ is a build-time global injected by vite.config.js's `define`
    // (reads package.json), not a real identifier — no import needed.
    this.el.querySelector('[data-action="play"]').addEventListener('click', () => { audio.playSfx('click'); onPlay(); });
    this.el.querySelector('[data-action="shop"]').addEventListener('click', () => { audio.playSfx('click'); onShop(); });
    this.el.querySelector('[data-action="settings-hub"]').addEventListener('click', () => { audio.playSfx('click'); onSettingsHub(); });

    const muteButton = this.el.querySelector('[data-action="mute"]');
    muteButton.addEventListener('click', () => {
      const nowMuted = !save.muted;
      save.setMuted(nowMuted);
      audio.setMuted(nowMuted);
      muteButton.textContent = nowMuted ? '\u{1F507}' : '\u{1F50A}';
      if (!nowMuted) audio.playSfx('click');
    });

    // Native Fullscreen API on <html> (not just #app) — takes over the whole
    // display in every major browser, which is what actually removes the OS
    // taskbar and the browser's own address/tab bar, not just resizes the
    // page within them. Must be triggered from a real click (Fullscreen API
    // requires a user gesture); state persists across every later screen
    // since those are all DOM swaps within this same page, not navigations.
    const fullscreenButton = this.el.querySelector('[data-action="fullscreen"]');
    fullscreenButton.addEventListener('click', () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        document.documentElement.requestFullscreen().then(() => requestLandscape()).catch(() => {});
      }
    });
    // Keeps the icon correct even when fullscreen is exited some other way
    // (Escape, F11, alt-tab on some platforms) instead of only this button.
    this._onFullscreenChange = () => {
      fullscreenButton.textContent = document.fullscreenElement ? '\u{1F5D9}' : '\u{26F6}';
    };
    document.addEventListener('fullscreenchange', this._onFullscreenChange);

    this.root.appendChild(this.el);
  }

  destroy() {
    document.removeEventListener('fullscreenchange', this._onFullscreenChange);
    this.el.remove();
  }
}
